import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { paymentService } from '../../api/paymentService';
import { userTestDepositFormFields as F } from '../../forms/fields/formFields/userTestDeposit';
import { userTestDepositSchema } from '../../forms/validationSchema/formSchema/userTestDeposit';

type UserTestDepositDialogProps = {
  open: boolean;
  userId: number;
  onClose: () => void;
};

/** The form, mounted per open — so every open starts empty and validated. */
const UserTestDepositForm = ({ userId, onClose }: Omit<UserTestDepositDialogProps, 'open'>) => {
  const { enqueueSnackbar } = useSnackbar();

  const { handleSubmit, values, handleChange, handleBlur, setFieldValue, touched, errors, isValid, isSubmitting } =
    useFormik({
      initialValues: { [F.amount]: '', [F.processNow]: true },
      validationSchema: userTestDepositSchema,
      validateOnMount: true,
      onSubmit: async (formValues) => {
        try {
          const response = await paymentService.createDummyUserStatement({
            userId,
            amount: Number(formValues[F.amount]),
          });
          const statementId = response.data?.statementId;

          if (formValues[F.processNow]) {
            const result = await paymentService.processPendingStatements();
            enqueueSnackbar(
              `Statement ${statementId ?? ''} processed — ${result.data?.processed ?? 0} processed, ${
                result.data?.errors ?? 0
              } errors`,
              { variant: result.data?.errors ? 'warning' : 'success' },
            );
          } else {
            enqueueSnackbar(`Statement ${statementId ?? ''} created — pending until the statements run`, {
              variant: 'success',
            });
          }

          onClose();
        } catch (error: any) {
          enqueueSnackbar(error?.message || 'Failed to add funds with ledger', { variant: 'error' });
        }
      },
    });

  const amountError = (touched[F.amount] && errors[F.amount]) || undefined;

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>Add funds (with ledger)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            Testing only — not available in production. Creates a fake bank deposit to this user, processed like a real
            one: ledger journals, balance, and auto-invest if the user has it on. Processing runs every pending
            statement, not only this one.
          </Alert>
          <TextField
            fullWidth
            size="small"
            id={F.amount}
            name={F.amount}
            label="Amount"
            type="number"
            value={values[F.amount]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(amountError)}
            helperText={amountError}
            InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
          />
          <FormControlLabel
            control={
              <Switch
                id={F.processNow}
                checked={Boolean(values[F.processNow])}
                onChange={(event) => setFieldValue(F.processNow, event.target.checked)}
              />
            }
            label="Process the pending statements now"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton type="submit" variant="contained" loading={isSubmitting} disabled={!isValid}>
          Add funds
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

/**
 * TESTING ONLY — puts money on a user's (B2C) virtual bank through the real deposit path: a dummy ANB incoming
 * statement the payment service resolves to the user's VIBAN, so the ledger and the balance move as they would for
 * a real transfer. Users only — companies use the treasury test top-up. madkhol-admin and madkhol-payment both
 * refuse it in PROD.
 */
export const UserTestDepositDialog = ({ open, ...props }: UserTestDepositDialogProps) => (
  <Dialog open={open} onClose={props.onClose} maxWidth="xs" fullWidth>
    {open && <UserTestDepositForm {...props} />}
  </Dialog>
);
